// Section 22 Title screen: New game (Section 3 form with presets), Continue, Load save (slots
// with turn, season and date), Import run (schema-validated), Settings, How to play.
import type { NewGameOptions } from '../../sim/api';
import type { Notice } from '../../store';
import type { NewGameFormVM, SaveRow, SavesVM } from '../../viewModels';
import { seasonName } from '../format';
import { text, uiText } from '../text/en';
import { NoticeBar } from '../shell/notices';
import { SaveList } from '../shell/save-list';
import { NewGameForm } from './new-game-form';
import './title.css';

interface Props {
  form: NewGameFormVM;
  saves: SavesVM;
  notice: Notice | null;
  onDismissNotice: () => void;
  onStart: (options: NewGameOptions, seed: string) => void;
  onContinue: () => void;
  onLoadSlot: (row: SaveRow) => void;
  onDeleteSlot: (row: SaveRow) => void;
  onLoadAutosave: (row: SaveRow) => void;
  onImport: (file: File) => void;
  onSettings: () => void;
  onHowToPlay: () => void;
}

export function TitleScreen(props: Props) {
  const { saves } = props;
  const t = uiText.title;
  const latest = saves.latest;
  return (
    <main class="title" id="work">
      <h1 class="title__name">{text.title}</h1>
      <p class="title__tagline">{text.tagline}</p>
      {props.notice !== null && <NoticeBar notice={props.notice} onDismiss={props.onDismissNotice} />}
      <nav class="title__actions" aria-label="Game">
        {latest !== null && (
          <button type="button" class="button button--primary" onClick={props.onContinue}>
            {t.continue}
            <span class="title__detail"> {t.continueDetail(seasonName(latest.season), latest.year, latest.seed)}</span>
          </button>
        )}
        <label class="button file-button">
          {t.import}
          <input
            type="file"
            accept=".json,application/json"
            class="visually-hidden"
            onChange={(e) => {
              const file = e.currentTarget.files?.[0];
              e.currentTarget.value = '';
              if (file !== undefined) props.onImport(file);
            }}
          />
        </label>
        <button type="button" class="button" onClick={props.onSettings}>
          {t.settings}
        </button>
        <button type="button" class="button" onClick={props.onHowToPlay}>
          {t.howToPlay}
        </button>
      </nav>
      <NewGameForm vm={props.form} onStart={props.onStart} />
      <section class="title__saves" aria-labelledby="load-title">
        <h2 id="load-title">{t.load}</h2>
        <SaveList
          rows={saves.slots}
          caption={uiText.saves.slots}
          empty={uiText.saves.noSlots}
          onLoad={props.onLoadSlot}
          onDelete={props.onDeleteSlot}
        />
        <h3>{uiText.saves.autosaves}</h3>
        <SaveList
          rows={saves.start === null ? saves.autosaves : [...saves.autosaves, saves.start]}
          caption={uiText.saves.autosaves}
          empty={uiText.saves.noAutosaves}
          onLoad={props.onLoadAutosave}
        />
      </section>
    </main>
  );
}
